import { useState, useRef } from 'react';
import type { Question, Option, QuestionType, QuestionParams, RichText, SliderParams, JumpLogic, Correlation, OptionLink, MatrixTextColumn, TextValidation } from './types';
import { isNonAnswerType, selectLimitOf, questionImages, MAX_QUESTION_IMAGES, isChoiceType } from './types';
import QuestionMenu from './QuestionMenu';
import AddQuestionModal from './AddQuestionModal';
import DisplayLogicModal from './DisplayLogicModal';
import JumpLogicModal from './JumpLogicModal';
import QuestionCorrelationModal from './QuestionCorrelationModal';
import OptionLinkModal from './OptionLinkModal';
import AdvancedModal from './AdvancedModal';
import RichTextEditor from './RichTextEditor';

type Props = {
  question: Question;
  index: number;
  /** 用户视角题号（statement / pagebreak 不计数）；可作答题从 1 开始 */
  displayIndex: number;
  allQuestions: Question[];
  onChange: (patch: Partial<Question>) => void;
  onRemove: () => void;
  onInsertBelow: (type: QuestionType) => void;
  /** 0902 新增：复制本题（在下方生成一模一样的题目） */
  onDuplicate?: () => void;
  /** 0902 新增：分页栏专用——把本页（分页栏之前）全部题目复制成新的一页插在其后 */
  onDuplicatePage?: () => void;
  /** 0904 新增：分页栏专用——删除本页（分页栏之前）的全部题目；分页栏自身保留 */
  onRemovePage?: () => void;
  /** 0901 新增：分页栏当前页码（编辑端从 1 开始计数；undefined 时退化为原题号） */
  pageNumber?: number;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent, index: number) => void;
  onDragOver?: (e: React.DragEvent, index: number) => void;
  onDragEnd?: () => void;
  onDrop?: (e: React.DragEvent, index: number) => void;
  isDragOver?: boolean;
  /** 0907 新增：题目菜单触发「批量删除」时打开按区间删除弹窗（Editor 提供的入口） */
  onAskBatchDelete?: () => void;
  /** 0909 新增：题目菜单触发「批量移动」时打开按区间移动弹窗 */
  onAskBatchMove?: () => void;
};

/**
 * 单题（截图 2 设计）：
 *   [N → ] 题干 input
 *           问题描述（可选）
 *           输入预览区（圆角输入框 / 圆角单选卡片组 / 滑动条）
 *           [必填]                              [⋮]
 */
export default function QuestionCard({
  question,
  index,
  displayIndex,
  allQuestions,
  onChange,
  onRemove,
  onInsertBelow,
  onDuplicate,
  onDuplicatePage,
  onRemovePage,
  pageNumber,
  draggable = false,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  isDragOver,
  onAskBatchDelete,
  onAskBatchMove,
}: Props) {
  const q = question;
  const [addOpen, setAddOpen] = useState(false);
  const [logicOpen, setLogicOpen] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [correlationOpen, setCorrelationOpen] = useState(false);
  // 0907 选项关联弹窗
  const [linksOpen, setLinksOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // 0902 多选题选数限制面板（默认收起；未设置时默认可全选）
  const [limitOpen, setLimitOpen] = useState(false);

  const options: Option[] = (q.options ?? []).map((o) =>
    typeof o === 'string' ? { value: o } : o,
  );

  const setTitle = (rich: RichText) => onChange({ title: rich });
  const setDescription = (v: string) => onChange({ description: v });
  const updateOption = (i: number, patch: Partial<Option>) => {
    const next = [...options];
    const prevValue = next[i]?.value;
    next[i] = { ...next[i], ...patch };
    const extra: Partial<Question> = {};
    // 0907 选项关联：本题选项改名时，同步规则里勾选的子选项（源题侧的引用由 Editor.updateQuestion 同步）
    if (typeof patch.value === 'string' && prevValue !== undefined && patch.value !== prevValue && Array.isArray(q.optionLinks)) {
      extra.optionLinks = q.optionLinks.map((l) => ({ ...l, options: l.options.map((v) => (v === prevValue ? (patch.value as string) : v)) }));
    }
    onChange({ options: next, ...extra });
  };
  const addOption = () =>
    onChange({ options: [...options, { value: `选项${options.length + 1}` }] });
  const removeOption = (i: number) => {
    const removed = options[i]?.value;
    const extra: Partial<Question> = {};
    // 0907 选项关联：删掉的选项从各条规则里移除；规则空了就整条去掉
    if (Array.isArray(q.optionLinks) && removed !== undefined) {
      const links = q.optionLinks.map((l) => ({ ...l, options: l.options.filter((v) => v !== removed) })).filter((l) => l.options.length > 0);
      extra.optionLinks = links.length ? links : undefined;
    }
    onChange({ options: options.filter((_, idx) => idx !== i), ...extra });
  };

  const conditionsCount = q.displayLogic?.conditions?.length ?? 0;
  // 0904 跳题逻辑计数：byOption 条目数 + 是否有无条件
  const jumpCount = (() => {
    const jl = q.jumpLogic;
    if (!jl) return 0;
    const byOpt = jl.byOption ? Object.keys(jl.byOption).length : 0;
    const un = jl.unconditional ? 1 : 0;
    return byOpt + un;
  })();
  // 1627 题目关联：配置的关联规则数
  const correlationCount = Array.isArray(q.correlation) ? q.correlation.length : 0;
  // 0907 选项关联：仅带选项的选择题（单选 / 多选 / 下拉 / 排序）可配；badge 显示绑定数
  const canLinkOptions = isChoiceType(q.type) || q.type === 'ranking';
  const optionLinkCount = Array.isArray(q.optionLinks) ? q.optionLinks.length : 0;
  const isStatement = q.type === 'statement';
  const isPagebreak = q.type === 'pagebreak';
  const nonAnswer = isNonAnswerType(q.type);

  return (
    <div
      data-question-id={q.id}
      className={`q-item${isDragOver ? ' is-drag-over' : ''}${isStatement ? ' is-statement' : ''}${isPagebreak ? ' is-pagebreak' : ''}`}
      draggable={draggable}
      onDragStart={(e) => {
        // 排除输入控件内的拖拽
        const target = e.target as HTMLElement;
        if (target.closest('input, textarea, button, .opt-del, .q-menu, .opt-image-cell, .q-title-image, .rich-text-input, .rich-text-toolbar')) {
          e.preventDefault();
          return;
        }
        onDragStart?.(e, index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(e, index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop?.(e, index);
      }}
      onDragEnd={() => onDragEnd?.()}
    >
      {!isPagebreak && (
        <div className="q-item-side">
          <span className="q-item-drag" title="拖动排序" aria-label="拖动排序">
            <IconDrag />
          </span>
          <span className="q-item-no">{isStatement ? '★' : displayIndex}</span>
          <span className="q-item-arrow">→</span>
        </div>
      )}

      <div className="q-item-main">
        {isPagebreak ? (
          <div className="q-pagebreak-marker">
            <span className="q-pagebreak-line" />
            <span className="q-pagebreak-label">{`第 ${pageNumber ?? index + 1} 页`}</span>
            {onDuplicatePage && (
              <button
                type="button"
                className="q-pagebreak-copy"
                onClick={onDuplicatePage}
                title="把本页（此分页栏之前）的全部题目复制一份，作为新的一页插在本分页栏之后"
              >
                <IconCopy /> 复制本页题目
              </button>
            )}
            {/* 0904 删除本页题目：紧贴「复制本页题目」右侧，红色警示样式防误点 */}
            {onRemovePage && (
              <button
                type="button"
                className="q-pagebreak-delete"
                onClick={onRemovePage}
                title="一键删除本页（此分页栏之前）的全部题目，分页栏自身保留"
              >
                <IconTrash /> 删除本页题目
              </button>
            )}
            <span className="q-pagebreak-line" />
          </div>
        ) : (
          <RichTextEditor
            value={q.title}
            onChange={setTitle}
            placeholder={isStatement ? '请输入标题说明的主标题' : '请输入题干'}
            inputClassName="q-title-input"
            ariaLabel="题目主标题"
            searchField="title"
          />
        )}
        {!isPagebreak && (
          <input
            className="q-desc-input"
            data-find-field="description"
            value={q.description ?? ''}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isStatement ? '副标题（可选）' : '问题描述（可选）'}
          />
        )}

        {!isPagebreak && (
          <QuestionImagesCell images={questionImages(q)} onChange={(images) => onChange({ images, image: undefined, descriptionImage: undefined })}>
            {q.type === 'multi' && (
              <button
                type="button"
                className={`opt-image-add q-limit-btn${limitOpen || selectLimitOf(q) ? ' is-on' : ''}`}
                onClick={() => setLimitOpen((v) => !v)}
                aria-expanded={limitOpen}
                title="设置最少 / 最多可选几项（不设置则默认可全选）"
              >
                <IconLimit /> 选数限制{selectLimitSummary(q)}
              </button>
            )}
          </QuestionImagesCell>
        )}
        {q.type === 'multi' && limitOpen && (
          <SelectLimitEditor
            params={q.params}
            optionCount={options.length}
            onChange={(patch) => onChange({ params: { ...(q.params ?? {}), ...patch } })}
          />
        )}
        {q.type === 'ranking' && (
          <SelectLimitEditor
            params={q.params}
            optionCount={options.length}
            onChange={(patch) => onChange({ params: { ...(q.params ?? {}), ...patch } })}
            hint="排序题：填写者点击选项即排到第 1 位、再点取消；至少 / 最多可选几项会显示在题目副标题位置，提交时数量不符会拦截。不填 = 不限。"
          />
        )}

        {!isPagebreak && (
        <div className="q-input-area">
          {q.type === 'text' && (
            <div className="q-input-box">待填写者输入</div>
          )}
          {q.type === 'longtext' && (
            <textarea
              className="q-input-box is-textarea"
              placeholder="待填写者输入"
              disabled
              rows={3}
            />
          )}

          {(q.type === 'single' || q.type === 'multi' || q.type === 'select' || q.type === 'ranking') && (
            <>
              <div className="q-radio-cards">
                {options.map((o, i) => (
                  <div key={i} className="q-opt-row">
                    <div className="q-radio-card">
                      <span className="dot" />
                      <input
                        className="opt-edit"
                        data-find-field={`option-${i}`}
                        value={o.value}
                        onChange={(e) => updateOption(i, { value: e.target.value })}
                        placeholder={`选项${i + 1}`}
                      />
                      {q.type !== 'ranking' && (
                        <>
                          <input
                            className="input"
                            type="number"
                            step="any"
                            style={{ width: 74, flex: 'none', padding: '4px 8px', fontSize: 13 }}
                            title="该选项分值（评分题用，留空不计分）"
                            placeholder="分值"
                            value={o.score ?? ''}
                            onChange={(e) =>
                              updateOption(i, { score: e.target.value === '' ? undefined : Number(e.target.value) })
                            }
                          />
                          {/* 0904 多选互斥开关：仅 multi 显示；勾选互斥选项时自动取消所有普通选项，反之亦然；与填空/分值同级 */}
                          {q.type === 'multi' && (
                            <button
                              type="button"
                              className={`opt-exclusive-toggle${o.exclusive ? ' is-on' : ''}`}
                              onClick={() => updateOption(i, { exclusive: !o.exclusive })}
                              aria-pressed={!!o.exclusive}
                              title={o.exclusive ? '该选项为互斥项：勾选后会自动取消所有普通选项' : '设为互斥项：勾选后会自动取消所有普通选项'}
                            >
                              <span className="opt-switch" aria-hidden="true" />
                              互斥
                            </button>
                          )}
                          {/* 0902 选项附属填空开关：开启后填写者选中该选项时，选项下方出现文本框 */}
                          <button
                            type="button"
                            className={`opt-text-toggle${o.allowText ? ' is-on' : ''}`}
                            onClick={() => updateOption(i, { allowText: !o.allowText })}
                            aria-pressed={!!o.allowText}
                            title={o.allowText ? '选项填空已开启：填写者选中该选项后会出现一个文本框，再点关闭' : '开启选项填空：填写者选中该选项后可补充文本'}
                          >
                            <span className="opt-switch" aria-hidden="true" />
                            填空
                          </button>
                        </>
                      )}
                      <OptionImageCell
                        image={o.image}
                        onChange={(image) => updateOption(i, { image })}
                      />
                      <button
                        type="button"
                        className="opt-del"
                        onClick={() => removeOption(i)}
                        disabled={options.length <= 1}
                        title="删除选项"
                      >
                        ✕
                      </button>
                    </div>
                    {o.allowText && (
                      <div className="q-opt-text-preview">
                        <span className="q-opt-text-preview-box">填写者选中「{o.value || `选项${i + 1}`}」后在此填写文本</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" className="q-add-opt" onClick={addOption}>
                + 添加选项
              </button>
            </>
          )}

          {q.type === 'slider' && (
            <SliderEditor
              params={q.params as SliderParams | undefined}
              onChange={(next) => onChange({ params: { ...(q.params ?? {}), ...next } })}
            />
          )}

          {q.type === 'matrix' && (
            <MatrixEditor
              rows={q.params?.rows ?? []}
              columns={q.params?.columns ?? []}
              columnScores={q.params?.columnScores ?? []}
              columnAllowText={q.params?.columnAllowText ?? []}
              onChange={(rows, columns, columnScores, columnAllowText) =>
                onChange({ params: { ...(q.params ?? {}), rows, columns, columnScores, columnAllowText } })
              }
            />
          )}

          {q.type === 'matrixtext' && (
            <MatrixTextEditor
              rows={q.params?.rows ?? []}
              params={q.params}
              onChange={(rows, patch) => onChange({ params: { ...(q.params ?? {}), rows, ...patch } })}
            />
          )}

          {q.type === 'matrixslider' && (
            <MatrixSliderEditor
              params={q.params}
              onChange={(patch) => onChange({ params: { ...(q.params ?? {}), ...patch } })}
            />
          )}

          {q.type === 'file' && (
            <div className="q-input-box">填写者可上传多张图片（每张≤4MB，自动压缩 / PDF ≤ 3MB）</div>
          )}

          {isStatement && (
            <div className="q-statement-hint">
              <span>标题说明</span>
              <em>填写者无需作答，仅作页面说明 / 章节分隔</em>
            </div>
          )}
        </div>
        )}

        {!isPagebreak && (
        <div className="q-actions">
          {!nonAnswer && (
            <button
              type="button"
              className={`q-chip ${q.required ? 'is-on' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange({ required: !q.required });
              }}
              aria-pressed={q.required}
            >
              必填
            </button>
          )}

          {(q.type === 'text' || q.type === 'longtext') && (
            <select
              className={`q-chip q-chip-select ${q.params?.validation ? 'is-on' : ''}`}
              value={q.params?.validation ?? ''}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                // 0903 填空题格式校验 / 0904 开放题同款：不满足格式禁止提交（前端拦截 + 服务端兜底）
                const v = e.target.value;
                const rest: Record<string, any> = { ...(q.params ?? {}) };
                delete rest.validation;
                onChange({ params: v ? { ...rest, validation: v } : rest });
              }}
              title={`${q.type === 'longtext' ? '开放题' : '填空题'}校验：填写者输入不满足格式时禁止提交；日期校验会在填写端弹出日历选择`}
              aria-label="填空校验"
            >
              <option value="">不校验</option>
              <option value="name">姓名校验</option>
              <option value="phone">手机号校验</option>
              <option value="idcard">身份证号校验</option>
              <option value="date">日期校验</option>
            </select>
          )}

          {onDuplicate && (
            <button
              type="button"
              className="q-chip"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDuplicate();
              }}
              title="在下方生成一道一模一样的题目"
            >
              复制题目
            </button>
          )}

          {!nonAnswer && (
            <button
              type="button"
              className={`q-chip ${q.onPreface ? 'is-on' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange({ onPreface: !q.onPreface });
              }}
              aria-pressed={q.onPreface}
              title="在前置页（协议/须知页）随基本信息一起填写"
            >
              前置页
            </button>
          )}

          <QuestionMenu
            logicCount={conditionsCount}
            onShowLogic={() => setLogicOpen(true)}
            onShowAdvanced={() => setAdvancedOpen(true)}
            onInsertBelow={() => setAddOpen(true)}
            onInsertStatementBelow={() => onInsertBelow('statement')}
            onInsertPagebreakBelow={() => onInsertBelow('pagebreak')}
            onRemove={onRemove}
            onBatchDelete={onAskBatchDelete}
            onAskBatchMove={onAskBatchMove}
            onShowJump={!nonAnswer ? () => setJumpOpen(true) : undefined}
            jumpCount={jumpCount}
            onShowOptionLinks={canLinkOptions ? () => setLinksOpen(true) : undefined}
            optionLinkCount={optionLinkCount}
            onShowCorrelation={!nonAnswer ? () => setCorrelationOpen(true) : undefined}
            correlationCount={correlationCount}
          />
        </div>
        )}

        {isPagebreak && (
          <div className="q-actions is-pagebreak-actions">
            <span className="hint">填写端将在此处切换到下一页</span>
            <button
              type="button"
              className="opt-del"
              onClick={onRemove}
              title="删除分页栏"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {logicOpen && (
        <DisplayLogicModal
          logic={q.displayLogic ?? { operator: 'AND', conditions: [] }}
          allQuestions={allQuestions}
          currentQid={q.id}
          onChange={(logic) => onChange({ displayLogic: logic })}
          onClose={() => setLogicOpen(false)}
        />
      )}

      {jumpOpen && (
        <JumpLogicModal
          jumpLogic={q.jumpLogic}
          allQuestions={allQuestions}
          currentQid={q.id}
          onChange={(logic: JumpLogic | undefined) => onChange({ jumpLogic: logic })}
          onClose={() => setJumpOpen(false)}
        />
      )}

      {correlationOpen && (
        <QuestionCorrelationModal
          correlation={q.correlation}
          allQuestions={allQuestions}
          currentQid={q.id}
          onChange={(corr: Correlation[] | undefined) => onChange({ correlation: corr })}
          onClose={() => setCorrelationOpen(false)}
        />
      )}

      {linksOpen && (
        <OptionLinkModal
          links={q.optionLinks}
          allQuestions={allQuestions}
          currentQid={q.id}
          onChange={(links: OptionLink[] | undefined) => onChange({ optionLinks: links })}
          onClose={() => setLinksOpen(false)}
        />
      )}

      {advancedOpen && (
        <AdvancedModal
          question={q}
          options={options}
          onChange={(patch) => onChange(patch)}
          onChangeOption={(i, patch) => {
            const next = [...options];
            next[i] = { ...next[i], ...patch };
            onChange({ options: next });
          }}
          onClose={() => setAdvancedOpen(false)}
        />
      )}

      {addOpen && (
        <AddQuestionModal
          onSelect={(t) => {
            setAddOpen(false);
            onInsertBelow(t);
          }}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

/** 图片读成 data URL；>300KB 的大图等比缩到 1280px 内转 JPEG，避免撑爆问卷配置 */
function compressImageFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (file.size <= 300 * 1024) {
        resolve(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        const max = 1280;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => reject(new Error('图片读取失败，请换一张试试'));
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

/** 题干配图（0903 起所有题型可挂多张，最多 MAX_QUESTION_IMAGES 张）：缩略图 + ✕ 删除 + 添加（可一次多选） */
function QuestionImagesCell({
  images,
  onChange,
  children,
}: {
  images: string[];
  onChange: (images: string[]) => void;
  /** 0902：跟在题干配图右侧的附加控件（如多选题「选数限制」） */
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFiles = async (files: File[]) => {
    const room = MAX_QUESTION_IMAGES - images.length;
    if (room <= 0) {
      alert(`每题最多 ${MAX_QUESTION_IMAGES} 张图片`);
      return;
    }
    const picked = files.slice(0, room);
    if (picked.length < files.length) alert(`每题最多 ${MAX_QUESTION_IMAGES} 张图片，本次只添加前 ${picked.length} 张`);
    const next = [...images];
    for (const f of picked) {
      if (f.size > 10 * 1024 * 1024) {
        alert(`「${f.name}」超过 10MB，已跳过`);
        continue;
      }
      try {
        next.push(await compressImageFile(f));
      } catch (e: any) {
        alert(e?.message || '图片读取失败');
      }
    }
    onChange(next);
  };
  return (
    <div className="q-title-image" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, margin: '2px 0 6px' }}>
      {images.length > 0 && (
        <div className="q-images">
          {images.map((src, i) => (
            <div className="q-image-item" key={`${src.slice(0, 32)}-${i}`}>
              <img src={src} alt={`题图 ${i + 1}`} />
              <button type="button" className="q-image-del" onClick={() => onChange(images.filter((_, idx) => idx !== i))} title="移除这张图片" aria-label="移除这张图片">✕</button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="opt-image-add"
        onClick={() => inputRef.current?.click()}
        disabled={images.length >= MAX_QUESTION_IMAGES}
        title={`为题目添加图片（可一次选多张，最多 ${MAX_QUESTION_IMAGES} 张）`}
      >
        <IconImage /> {images.length ? `添加图片（${images.length}/${MAX_QUESTION_IMAGES}）` : '题干配图'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) void handleFiles(files);
          e.target.value = '';
        }}
      />
      {children}
    </div>
  );
}

/** 矩阵量表的行/列编辑：行为项目列表；列为量表刻度（可配分值，用于评分统计）
 *  1627 起每列可独立开启「填空」：开启后填写者选中该列时，行单元格下方出现文本框（存 _matrixColText） */
function MatrixEditor({
  rows,
  columns,
  columnScores,
  columnAllowText,
  onChange,
}: {
  rows: string[];
  columns: string[];
  columnScores: (number | null)[];
  columnAllowText?: boolean[];
  onChange: (rows: string[], columns: string[], columnScores: (number | null)[], columnAllowText: boolean[]) => void;
}) {
  const scores = columns.map((_, i) => (columnScores[i] === undefined ? null : columnScores[i]));
  // columnAllowText 与 columns 等长；新增列默认 false
  const allowText = columns.map((_, i) => Boolean(columnAllowText?.[i]));
  const setAllowTextAt = (i: number, v: boolean) => {
    const next = allowText.map((x, idx) => (idx === i ? v : x));
    onChange(rows, columns, scores, next);
  };
  const editStr = (list: string[], i: number, v: string) => list.map((x, idx) => (idx === i ? v : x));
  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div className="qe-label" style={{ marginBottom: 6 }}>矩阵行（评价项目）</div>
        {rows.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input className="opt-edit" data-find-field={`row-${i}`} value={v} onChange={(e) => onChange(editStr(rows, i, e.target.value), columns, scores, allowText)} placeholder={`项目${i + 1}`} />
            <button type="button" className="opt-del" onClick={() => onChange(rows.filter((_, idx) => idx !== i), columns, scores, allowText)} disabled={rows.length <= 1} title="删除项目">✕</button>
          </div>
        ))}
        <button type="button" className="q-add-opt" onClick={() => onChange([...rows, `项目${rows.length + 1}`], columns, scores, allowText)}>+ 添加项目</button>
      </div>
      <div style={{ flex: 1.2, minWidth: 230 }}>
        <div className="qe-label" style={{ marginBottom: 6 }}>矩阵列（量表刻度 · 分值可选）</div>
        {columns.map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="opt-edit" data-find-field={`column-${i}`} value={v} onChange={(e) => onChange(rows, editStr(columns, i, e.target.value), scores, allowText)} placeholder={`刻度${i + 1}`} style={{ flex: '1 1 100px' }} />
            <input
              className="input"
              type="number"
              step="any"
              style={{ width: 70, flex: 'none', padding: '4px 8px', fontSize: 13 }}
              title="该刻度分值（留空不计分）"
              placeholder="分值"
              value={scores[i] ?? ''}
              onChange={(e) => onChange(rows, columns, scores.map((s, idx) => (idx === i ? (e.target.value === '' ? null : Number(e.target.value)) : s)), allowText)}
            />
            {/* 1627 矩阵列填空 toggle：跟选项 allowText 同款，勾选后填写者选中该列会出现文本框 */}
            <button
              type="button"
              className={`opt-text-toggle${allowText[i] ? ' is-on' : ''}`}
              onClick={() => setAllowTextAt(i, !allowText[i])}
              aria-pressed={allowText[i]}
              title={allowText[i] ? '该列填空已开启：填写者选中该列后该行出现文本框' : '开启列填空：填写者选中该列后该行出现文本框'}
            >
              <span className="opt-switch" aria-hidden="true" />
              填空
            </button>
            <button
              type="button"
              className="opt-del"
              onClick={() => onChange(rows, columns.filter((_, idx) => idx !== i), scores.filter((_, idx) => idx !== i), allowText.filter((_, idx) => idx !== i))}
              disabled={columns.length <= 1}
              title="删除刻度"
            >
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="q-add-opt" onClick={() => onChange(rows, [...columns, `刻度${columns.length + 1}`], [...scores, null], [...allowText, false])}>+ 添加刻度</button>
      </div>
    </div>
  );
}

/** 滑动条设置：最小值/最大值/数值间隔/不允许小数 + 两端标签文本 */
function SliderEditor({
  params,
  onChange,
}: {
  params?: SliderParams;
  onChange: (next: Partial<SliderParams>) => void;
}) {
  const min = params?.min ?? 0;
  const max = params?.max ?? 100;
  const step = params?.step ?? 1;
  const allowDecimal = !!params?.allowDecimal;
  const minLabel = params?.minLabel ?? '';
  const maxLabel = params?.maxLabel ?? '';
  const unit = params?.unit ?? '';
  // 实际区间长度（小数位数提示）
  const decimals = useDecimals(step);

  return (
    <div className="q-slider-editor">
      <div className="q-slider-preview">
        <div className="q-slider-preview-top">
          <span className="q-slider-preview-label">{minLabel || (allowDecimal ? min : min)}</span>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            defaultValue={(min + max) / 2}
            disabled
          />
          <span className="q-slider-preview-label">{maxLabel || max}</span>
        </div>
        <div className="q-slider-preview-bottom">
          {Array.from({ length: 6 }).map((_, i) => {
            const span = max - min;
            const v = min + (span * i) / 5;
            const snap = allowDecimal ? Number(v.toFixed(decimals)) : Math.round(v);
            return <span key={i}>{snap}</span>;
          })}
        </div>
      </div>

      <div className="q-slider-fields">
        <label className="qe-line">
          最小值
          <input
            type="number"
            step={allowDecimal ? 'any' : 1}
            className="input"
            value={min}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value);
              onChange({ min: v });
            }}
          />
        </label>
        <label className="qe-line">
          最大值
          <input
            type="number"
            step={allowDecimal ? 'any' : 1}
            className="input"
            value={max}
            onChange={(e) => {
              const v = e.target.value === '' ? 0 : Number(e.target.value);
              onChange({ max: v });
            }}
          />
        </label>
        <label className="qe-line">
          数值间隔
          <input
            type="number"
            step={allowDecimal ? 'any' : 1}
            min={allowDecimal ? 0.0001 : 1}
            className="input"
            value={step}
            onChange={(e) => {
              const v = e.target.value === '' ? 1 : Number(e.target.value);
              if (!allowDecimal && (!Number.isInteger(v) || v < 1)) {
                alert('不允许小数时数值间隔必须是 ≥1 的整数');
                return;
              }
              onChange({ step: v });
            }}
          />
        </label>
        <label className="qe-line qe-checkbox">
          <input
            type="checkbox"
            checked={!allowDecimal}
            onChange={(e) => {
              const disallow = e.target.checked;
              if (disallow) {
                // 切回"不允许小数"时把 step 规整到 ≥1 整数
                const snapped = Math.max(1, Math.round(step));
                onChange({ allowDecimal: false, step: snapped });
              } else {
                onChange({ allowDecimal: true });
              }
            }}
          />
          不允许小数
        </label>
        <label className="qe-line">
          最小值显示文本
          <input
            type="text"
            className="input"
            maxLength={24}
            value={minLabel}
            data-find-field="minLabel"
            placeholder="如：不满意（1）"
            onChange={(e) => onChange({ minLabel: e.target.value })}
          />
        </label>
        <label className="qe-line">
          最大值显示文本
          <input
            type="text"
            className="input"
            maxLength={24}
            value={maxLabel}
            data-find-field="maxLabel"
            placeholder="如：满意（15）"
            onChange={(e) => onChange({ maxLabel: e.target.value })}
          />
        </label>
        <label className="qe-line">
          单位（可选）
          <input
            type="text"
            className="input"
            maxLength={6}
            value={unit}
            data-find-field="unit"
            placeholder="如：分、℃"
            onChange={(e) => onChange({ unit: e.target.value })}
          />
        </label>
      </div>
    </div>
  );
}

function useDecimals(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  const s = String(step);
  const idx = s.indexOf('.');
  if (idx < 0) return 0;
  return Math.min(4, s.length - idx - 1);
}

function IconDrag() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="9" cy="6" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="9" cy="18" r="1.5" />
      <circle cx="15" cy="6" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="15" cy="18" r="1.5" />
    </svg>
  );
}

/** 选项的图片上传格：缩略图 / +图片 / ✕ */
function OptionImageCell({
  image,
  onChange,
}: {
  image?: string;
  onChange: (image: string | undefined) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFile = (file: File) => {
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      alert('图片不能超过 1.5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result as string);
    reader.readAsDataURL(file);
  };
  return (
    <div className="opt-image-cell">
      {image ? (
        <>
          <img src={image} alt="选项图" className="opt-thumb" />
          <button
            type="button"
            className="opt-image-del"
            onClick={() => onChange(undefined)}
            title="移除图片"
          >
            ✕
          </button>
        </>
      ) : (
        <button
          type="button"
          className="opt-image-add"
          onClick={() => inputRef.current?.click()}
          title="添加图片"
        >
          <IconImage /> 图片
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = ''; // 允许重复选同一张
        }}
      />
    </div>
  );
}

function IconImage() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="M21 15l-5-5L5 21" />
    </svg>
  );
}

/** 0902 多选题选数限制的按钮摘要："（最少2项 · 最多3项）"，未设置为空 */
function selectLimitSummary(q: Question): string {
  const l = selectLimitOf(q);
  if (!l) return '';
  const parts: string[] = [];
  if (l.min !== null) parts.push(`最少${l.min}项`);
  if (l.max !== null) parts.push(`最多${l.max}项`);
  return `（${parts.join(' · ')}）`;
}

/** 0902 多选题选数限制：两行——最少选择数 / 最多选择数，各带可配的提示文案（留空用默认文案） */
function SelectLimitEditor({
  params,
  optionCount,
  onChange,
  hint,
}: {
  params?: QuestionParams;
  optionCount: number;
  onChange: (patch: Partial<QuestionParams>) => void;
  /** 底部说明文案（排序题与多选题措辞不同） */
  hint?: string;
}) {
  const min = params?.minSelect;
  const max = params?.maxSelect;
  // 空 / 0 / 非法 → undefined（不限）
  const toCount = (raw: string): number | undefined => {
    if (raw === '') return undefined;
    const n = Math.floor(Number(raw));
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  const minTooBig = !!min && !!max && min > max;
  const minOverOptions = !!min && optionCount > 0 && min > optionCount;
  return (
    <div className="q-select-limit">
      <div className="q-select-limit-row">
        <span>最少选择数</span>
        <input
          className="input"
          type="number"
          min={1}
          max={optionCount || undefined}
          placeholder="不限"
          value={min ?? ''}
          onChange={(e) => onChange({ minSelect: toCount(e.target.value) })}
        />
        <span>提示文案</span>
        <input
          className="input"
          type="text"
          maxLength={40}
          placeholder={min ? `至少选择${min}项` : '例：至少选择2项'}
          value={params?.minSelectHint ?? ''}
          data-find-field="minSelectHint"
          onChange={(e) => onChange({ minSelectHint: e.target.value || undefined })}
        />
      </div>
      <div className="q-select-limit-row">
        <span>最多选择数</span>
        <input
          className="input"
          type="number"
          min={1}
          max={optionCount || undefined}
          placeholder="不限"
          value={max ?? ''}
          onChange={(e) => onChange({ maxSelect: toCount(e.target.value) })}
        />
        <span>提示文案</span>
        <input
          className="input"
          type="text"
          maxLength={40}
          placeholder={max ? `最多只能选择${max}项` : '例：最多只能选择3项'}
          value={params?.maxSelectHint ?? ''}
          data-find-field="maxSelectHint"
          onChange={(e) => onChange({ maxSelectHint: e.target.value || undefined })}
        />
      </div>
      {minTooBig && <div className="hint hint-bad">最少选择数不能大于最多选择数（保存会被拒绝）</div>}
      {minOverOptions && <div className="hint hint-bad">最少选择数不能超过选项数量（当前 {optionCount} 项）</div>}
      <div className="hint">{hint ?? '不填 = 不限制（默认可全选）。填写端勾选达到上限后其余选项不可再勾选；提交时数量不符会拦截并弹出上面的提示文案。'}</div>
    </div>
  );
}

/** 0902 矩阵填空：左列行标题（可编辑），右列为填写者的填空框占位；支持添加一行、拖拽手柄排序
 *  0904 起在「填写者作答」与「删除该行」之间插入每行独立的校验下拉（姓名 / 手机号 / 身份证号 / 0907 日期）
 *  0907 起支持多列：每列可单独设置控件类型（文本输入框 / 下拉选择框，可混用）；下拉列录入选项列表。
 *  未显式配置列时沿用老的单列「填写者作答」（答案 { 行: 文本 }）；一旦改动列设置就写 params.textColumns（答案 { 行: { 列: 文本 } }）。 */
const DEFAULT_TEXT_COLUMN: MatrixTextColumn = { title: '填写者作答', kind: 'text' };

function MatrixTextEditor({ rows, params, onChange }: { rows: string[]; params?: QuestionParams; onChange: (rows: string[], patch: Partial<QuestionParams>) => void }) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const reset = () => {
    setDragIdx(null);
    setOverIdx(null);
  };
  const move = (from: number, to: number) => {
    if (from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next, {});
  };
  const rowValidations: Record<string, TextValidation> = (params?.rowValidations as any) ?? {};
  const setRowValidation = (r: string, v: string) => {
    const next: Record<string, TextValidation> = { ...rowValidations };
    if (v) next[r] = v as TextValidation;
    else delete next[r];
    onChange(rows, { rowValidations: Object.keys(next).length ? next : undefined });
  };
  // 0907 按行独立控件：每行可单独选 text/select；select 时行下方可录入 options
  const rowControls: Record<string, MatrixRowControl> = (params?.rowControls as any) ?? {};
  const setRowControlPatch = (r: string, patch: Partial<MatrixRowControl>) => {
    const cur: MatrixRowControl = rowControls[r] ?? { kind: 'text' };
    const next: Record<string, MatrixRowControl> = { ...rowControls, [r]: { ...cur, ...patch } };
    onChange(rows, { rowControls: Object.keys(next).length ? next : undefined });
  };
  const setRowControlKind = (r: string, kind: 'text' | 'select') => {
    const cur = rowControls[r] ?? { kind: 'text' };
    if (kind === 'select') {
      const opts = cur.kind === 'select' && cur.options?.length ? cur.options : ['选项1', '选项2'];
      setRowControlPatch(r, { kind, options: opts });
    } else {
      setRowControlPatch(r, { kind });
    }
  };
  // 改名：rowValidations + rowControls 跟着迁移
  const renameRow = (i: number, title: string) => {
    const old = rows[i];
    const nextRows = rows.map((x, idx) => (idx === i ? title : x));
    if (old !== title) {
      const patch: Partial<QuestionParams> = {};
      if (rowValidations[old] !== undefined) {
        const next: Record<string, TextValidation> = { ...rowValidations };
        next[title] = next[old];
        delete next[old];
        patch.rowValidations = next;
      }
      if (rowControls[old] !== undefined) {
        const next: Record<string, MatrixRowControl> = { ...rowControls };
        next[title] = next[old];
        delete next[old];
        patch.rowControls = next;
      }
      onChange(nextRows, patch);
    } else {
      onChange(nextRows, {});
    }
  };
  // 删行：rowValidations + rowControls 同步清理
  const removeRow = (i: number) => {
    const removed = rows[i];
    const nextRows = rows.filter((_, idx) => idx !== i);
    const patch: Partial<QuestionParams> = {};
    if (rowValidations[removed]) {
      const next: Record<string, TextValidation> = { ...rowValidations };
      delete next[removed];
      patch.rowValidations = Object.keys(next).length ? next : undefined;
    }
    if (rowControls[removed]) {
      const next: Record<string, MatrixRowControl> = { ...rowControls };
      delete next[removed];
      patch.rowControls = Object.keys(next).length ? next : undefined;
    }
    onChange(nextRows, patch);
  };

  // 0907 按行独立控件：下拉行可展开录入选项（行 idx → 展开状态）
  const [openOpts, setOpenOpts] = useState<Set<number>>(new Set());
  const toggleOpts = (i: number) =>
    setOpenOpts((cur) => {
      const n = new Set(cur);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });
  const setRowOptions = (r: string, options: string[]) => setRowControlPatch(r, { options });
  const addRowOption = (r: string) => {
    const cur = rowControls[r] ?? { kind: 'select' };
    const opts = cur.options ?? [];
    setRowOptions(r, [...opts, `选项${opts.length + 1}`]);
  };
  const updateRowOption = (r: string, j: number, value: string) => {
    const cur = rowControls[r] ?? { kind: 'select' };
    setRowOptions(r, (cur.options ?? []).map((x, k) => (k === j ? value : x)));
  };
  const removeRowOption = (r: string, j: number) => {
    const cur = rowControls[r] ?? { kind: 'select' };
    setRowOptions(r, (cur.options ?? []).filter((_, k) => k !== j));
  };
  return (
    <div className="q-matrixtext">
      <div className="qe-label">每行独立配置控件（文本输入框 / 下拉选择框，可混用）；下拉行展开后可录入选项。校验规则只校验输入内容，不改控件类型。</div>
      <div className="q-matrixtext-head">
        <span></span>
        <span>行标题</span>
        <span>控件</span>
        <span>填写者作答</span>
        <span className="q-matrixtext-head-validate">校验</span>
        <span></span>
      </div>
      {rows.map((r, i) => {
        const ctrl: MatrixRowControl = rowControls[r] ?? { kind: 'text' };
        const optsOpen = openOpts.has(i);
        return (
          <div
            key={i}
            className={`q-matrixtext-row${overIdx === i && dragIdx !== null && dragIdx !== i ? ' is-drag-over' : ''}${dragIdx === i ? ' is-dragging' : ''}${ctrl.kind === 'select' ? ' is-select' : ''}`}
            onDragOver={(e) => {
              // 只处理本组件内的行拖拽；阻止冒泡避免触发外层题目卡片的排序
              if (dragIdx === null) return;
              e.preventDefault();
              e.stopPropagation();
              if (overIdx !== i) setOverIdx(i);
            }}
            onDrop={(e) => {
              if (dragIdx === null) return;
              e.preventDefault();
              e.stopPropagation();
              move(dragIdx, i);
              reset();
            }}
          >
            <span
              className="q-item-drag q-matrixtext-drag"
              title="拖动排序"
              aria-label="拖动排序"
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = 'move';
                setDragIdx(i);
              }}
              onDragEnd={(e) => {
                e.stopPropagation();
                reset();
              }}
            >
              <IconDrag />
            </span>
            <input
              className="opt-edit q-matrixtext-title"
              value={r}
              data-find-field={`row-${i}`}
              placeholder={`行${i + 1}`}
              aria-label={`第 ${i + 1} 行标题`}
              onChange={(e) => renameRow(i, e.target.value)}
            />
            <select
              className="q-chip q-chip-select q-matrixtext-control"
              value={ctrl.kind}
              onChange={(e) => setRowControlKind(r, e.target.value as 'text' | 'select')}
              title="该行的填写控件类型"
              aria-label={`${r} 行控件`}
            >
              <option value="text">文本输入框</option>
              <option value="select">下拉选择框</option>
            </select>
            {ctrl.kind === 'select' ? (
              <div className="q-input-box q-matrixtext-answer q-matrixtext-cell-select" title={`下拉选项：${(ctrl.options ?? []).join(' / ')}`}>
                <span>请选择</span>
                <small>{(ctrl.options ?? []).length} 项 ▾</small>
              </div>
            ) : (
              <div className="q-input-box q-matrixtext-answer">
                {rowValidations[r] === 'date' ? '📅 选择日期' : '待填写者输入'}
              </div>
            )}
            <select
              className="q-chip q-chip-select q-matrixtext-validate"
              value={rowValidations[r] ?? ''}
              onChange={(e) => setRowValidation(r, e.target.value)}
              title="该行填空格式校验：不满足格式禁止提交；日期会弹日历选择"
              aria-label={`${r} 行校验`}
            >
              <option value="">不校验</option>
              <option value="name">姓名</option>
              <option value="phone">手机号</option>
              <option value="idcard">身份证号</option>
              <option value="date">日期</option>
            </select>
            <button
              type="button"
              className="opt-del"
              onClick={() => removeRow(i)}
              disabled={rows.length <= 1}
              title="删除该行"
            >
              ✕
            </button>
            {ctrl.kind === 'select' && (
              <div className="q-matrixtext-row-opts">
                <button type="button" className="q-mt-toggle-opts" onClick={() => toggleOpts(i)}>
                  {optsOpen ? '▲ 收起选项' : `▼ 录入选项（${(ctrl.options ?? []).length}）`}
                </button>
                {optsOpen && (
                  <div className="q-mt-row-opts">
                    {(ctrl.options ?? []).map((o, j) => (
                      <span key={j} className="q-mt-col-opt">
                        <input
                          value={o}
                          data-find-field={`row-${i}-option-${j}`}
                          placeholder={`选项${j + 1}`}
                          style={{ width: `${Math.max(3, o.length + 1)}em` }}
                          onChange={(e) => updateRowOption(r, j, e.target.value)}
                          aria-label={`${r} 行下拉选项 ${j + 1}`}
                        />
                        <button type="button" className="opt-del" onClick={() => removeRowOption(r, j)} disabled={(ctrl.options ?? []).length <= 1} title="删除选项">✕</button>
                      </span>
                    ))}
                    <button type="button" className="q-add-opt" onClick={() => addRowOption(r)}>+ 选项</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <button type="button" className="q-add-opt" onClick={() => onChange([...rows, `行${rows.length + 1}`], {})}>
        + 添加一行
      </button>
    </div>
  );
}

/** 0907 矩阵滑动题：多行（每行可编辑文本 + 上传配图，图显示在该行左侧）；每行绑定独立滑动条，分数范围共用（与滑动条题同款设置） */
function MatrixSliderEditor({ params, onChange }: { params?: QuestionParams; onChange: (patch: Partial<QuestionParams>) => void }) {
  const rows: string[] = Array.isArray(params?.rows) ? (params!.rows as string[]) : [];
  const images: (string | null)[] = rows.map((_, i) => (typeof params?.rowImages?.[i] === 'string' ? (params!.rowImages![i] as string) : null));
  const setRows = (nextRows: string[], nextImages: (string | null)[]) =>
    onChange({ rows: nextRows, rowImages: nextImages.some((x) => !!x) ? nextImages : undefined });
  return (
    <div className="q-matrixslider">
      <div className="qe-label">矩阵行 · 每行一个独立滑动条；行配图显示在该行左侧（可选）</div>
      <div className="q-matrixslider-rows">
        {rows.map((r, i) => (
          <div key={i} className="q-matrixslider-row">
            <RowImageCell image={images[i]} onChange={(img) => setRows(rows, images.map((x, idx) => (idx === i ? img : x)))} />
            <input
              className="opt-edit q-matrixtext-title"
              value={r}
              data-find-field={`row-${i}`}
              placeholder={`行${i + 1}`}
              onChange={(e) => setRows(rows.map((x, idx) => (idx === i ? e.target.value : x)), images)}
              aria-label={`第 ${i + 1} 行文本`}
            />
            <button
              type="button"
              className="opt-del"
              onClick={() => setRows(rows.filter((_, idx) => idx !== i), images.filter((_, idx) => idx !== i))}
              disabled={rows.length <= 1}
              title="删除该行"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="q-add-opt" onClick={() => setRows([...rows, `行${rows.length + 1}`], [...images, null])}>
        + 添加一行
      </button>
      <div className="qe-label">滑块分数范围（各行共用，逻辑与滑动条题一致）</div>
      <SliderEditor params={params as SliderParams | undefined} onChange={(next) => onChange(next)} />
    </div>
  );
}

/** 矩阵滑动题的行配图：缩略图 / +图片 / ✕；大图等比压缩后存 data URL */
function RowImageCell({ image, onChange }: { image: string | null; onChange: (image: string | null) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handleFile = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      alert('图片不能超过 10MB');
      return;
    }
    try {
      onChange(await compressImageFile(file));
    } catch (e: any) {
      alert(e?.message || '图片读取失败');
    }
  };
  return (
    <div className="opt-image-cell">
      {image ? (
        <>
          <img src={image} alt="行配图" className="opt-thumb" />
          <button type="button" className="opt-image-del" onClick={() => onChange(null)} title="移除图片">✕</button>
        </>
      ) : (
        <button type="button" className="opt-image-add" onClick={() => inputRef.current?.click()} title="为该行添加图片（显示在行左侧）">
          <IconImage /> 图片
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function IconCopy() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

function IconLimit() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M4 12h9M4 17h6" />
      <circle cx="17.5" cy="16" r="3" />
      <path d="M17.5 14.6v1.4l1 .8" />
    </svg>
  );
}
